import api from '@/services/apiService';
import type { GameDetailResponse, CustomFormField, CustomFormAnswerItem } from '@/services/communityService';

async function recapDownloadError(error: unknown, fallback: string): Promise<Error> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { message?: unknown };
      if (typeof parsed.message === 'string' && parsed.message.trim()) {
        return new Error(parsed.message.trim());
      }
    } catch {
      return new Error(fallback);
    }
  }
  if (error instanceof Error && error.message && !error.message.startsWith('Request failed with status code')) {
    return error;
  }
  return new Error(fallback);
}
export type { CustomFormField, CustomFormAnswerItem };

// Player role for event config
export interface PlayerRolePayload {
  role: string;
  slots: number;
  price: number;
}

export interface SparringTeam {
  name: string;
  jersey_color?: string;
}

// Event config
export interface EventConfig {
  allow_guest: boolean;
  auto_confirm: boolean;
  max_guests_per_user: number;
  player_roles: PlayerRolePayload[];
  is_sparring?: boolean;
  teams?: SparringTeam[];
  facilities?: string[];
  waiting_list?: boolean;
  waiting_list_slots?: number;
}

// Create event request payload
export interface CreateEventPayload {
  name: string;
  description: string;
  tnc: string;
  event_date: string;
  start_time: string;
  end_time: string;
  lokasi_arena: string;
  address: string;
  city: string;
  map_link: string;
  link_group: string;
  field_id: number | null;
  sport_category_id?: number;
  sport_category_slug?: string;
  visibility: 'public' | 'private';
  type: 'free' | 'paid';
  config: EventConfig;
  custom_form?: CustomFormField[];
}

// API response interface
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
}

// Event response (adjust based on actual API response)
export interface MabarEvent {
  id: number;
  name: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  lokasi_arena: string;
  city: string;
  type: 'free' | 'paid';
  visibility: 'public' | 'private';
  status: string;
  custom_form?: CustomFormField[];
  created_at: string;
}

// Sport category in event
export interface SportCategoryInfo {
  name: string;
  slug: string;
}

// Role availability info
export interface RoleAvailability {
  role: string;
  total: number;
  filled: number;
  available: number;
  price: number;
  team_member_counts?: Record<string, number>;
}

export interface Participant {
  name: string;
  avatar: string;
  role?: string;
  team?: string;
  status?: string;
  custom_form_answer?: CustomFormAnswerItem[];
}

// Public event from API (for /mabar/events listing)
export interface PublicMabarEvent {
  slug: string;
  name: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  city: string;
  address: string;
  lokasi_arena: string;
  map_link: string;
  sport_category: SportCategoryInfo;
  visibility: 'public' | 'private';
  type: 'free' | 'paid';
  status: string;
  is_host: boolean;
  is_joined?: boolean;
  isJoined?: boolean;
  host: {
    name: string;
    avatar: string;
    role: string;
    slug: string;
  };
  total_slots: number;
  filled_slots: number;
  available_slots: number;
  slot_percentage: number;
  roles_availability: RoleAvailability[];
  participants: Participant[];
  custom_form?: CustomFormField[];
  created_at: string;
}

/**
 * Helper to check if an event's date and time is in the past compared to local current time.
 * An event is past ONLY if the current time has passed the event's end_time (or start_time if end_time is omitted).
 */
export const isEventPast = (
  eventDate?: string,
  startTime?: string,
  endTime?: string
): boolean => {
  const targetTimeStr = endTime || startTime;

  // 1. If we have ISO datetime string (e.g. "2026-09-14T20:00:00+07:00"), compare timestamp directly with end_time
  if (targetTimeStr && targetTimeStr.includes('T') && targetTimeStr.length >= 19) {
    const d = new Date(targetTimeStr);
    if (!isNaN(d.getTime())) {
      return d.getTime() < Date.now();
    }
  }

  if (!eventDate) return false;

  const rawDateStr = eventDate.split('T')[0].trim();
  let year = 0;
  let month = 0;
  let day = 0;

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawDateStr)) {
    const parts = rawDateStr.split('-').map((n) => parseInt(n, 10));
    year = parts[0];
    month = parts[1];
    day = parts[2];
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(rawDateStr)) {
    const parts = rawDateStr.split('-').map((n) => parseInt(n, 10));
    day = parts[0];
    month = parts[1];
    year = parts[2];
  } else {
    const d = new Date(rawDateStr);
    if (!isNaN(d.getTime())) {
      year = d.getFullYear();
      month = d.getMonth() + 1;
      day = d.getDate();
    }
  }

  if (!year || !month || !day) return false;

  const formattedEventDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;

  if (formattedEventDate < todayStr) return true;
  if (formattedEventDate > todayStr) return false;

  // Same day: check end time!
  if (!targetTimeStr) return false;

  try {
    let hours = 0;
    let minutes = 0;

    if (targetTimeStr.includes('T')) {
      const timePart = targetTimeStr.split('T')[1];
      const parts = timePart.split(':');
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
    } else {
      const parts = targetTimeStr.match(/(\d{1,2})[:.](\d{2})/);
      if (parts) {
        hours = parseInt(parts[1], 10);
        minutes = parseInt(parts[2], 10);
      }
    }

    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();

    if (currentHours > hours) return true;
    if (currentHours === hours && currentMinutes >= minutes) return true;
    return false;
  } catch {
    return false;
  }
};

// Query params for public events listing
export interface PublicEventsQueryParams {
  city?: string;
  sport_category?: string;
  sport_type?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  host_id?: number | null;
  page?: number;
  limit?: number;
  lp?: string;
  type?: string;
}

// Participation event from API
export interface ParticipationEvent {
  slug: string;
  name: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  city: string;
  address: string;
  lokasi_arena: string;
  map_link: string;
  sport_category: SportCategoryInfo;
  visibility: 'public' | 'private';
  type: 'free' | 'paid';
  link_group?: string;
  status: string;
  total_slots: number;
  filled_slots: number;
  available_slots: number;
  slot_percentage: number;
  roles_availability: RoleAvailability[];
  participants: Participant[];
  created_at: string;
  is_host: boolean;
  my_role?: string;
  my_status?: string;
  my_price?: number;
  joined_at?: string;
  auto_confirm: boolean;
  my_payment_status?: string;
  my_invoice_url?: string;
  my_payment_method?: string;
  my_order_number?: string;
  // For host events
  pending_count?: number;
  confirmed_count?: number;
}

// Host info
export interface HostInfo {
  name: string;
  avatar: string;
  slug?: string;
}

// Event detail response
export interface EventDetailResponse {
  slug: string;
  name: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  city: string;
  address: string;
  lokasi_arena: string;
  map_link: string;
  sport_category: SportCategoryInfo;
  visibility: 'public' | 'private';
  type: 'free' | 'paid';
  status: string;
  is_host: boolean;
  total_slots: number;
  filled_slots: number;
  available_slots: number;
  slot_percentage: number;
  roles_availability: RoleAvailability[];
  participants: Participant[];
  created_at: string;
  tnc: string;
  link_group: string;
  host: HostInfo;
  config: EventConfig;
  custom_form?: CustomFormField[];
}

// Game list item for event games
export interface GameListItem {
  id: number;
  slug: string;
  home_team_name: string;
  away_team_name: string;
  start_time?: string | null;
  end_time?: string | null;
  home_score?: number;
  away_score?: number;
  scorers_summary?: string;
  timeline?: any[];
}

export interface StandingItem {
  position: number;
  team: string;

  matches_played: number;

  wins: number;
  draws: number;
  losses: number;

  goals_for: number;
  goals_against: number;
  goal_difference: number;

  points: number;
}

// Event my stats (for customer event hasil page)
export interface EventMyStatsResponse {
  goals: number;
  assists: number;
  win_rate: number;
  matches: number;
  clean_sheet?: number;
  point?: number;
  point_loss?: number;
  is_racket?: boolean;
  delta_mmr?: number | null;
  has_mmr?: boolean;
  key_passes?: number;
  saves?: number;
  clearances?: number;
  fouls?: number;
  motm?: number;
  freestyle?: number;
  winstreak?: number;
}

// Update event request payload
export interface UpdateEventPayload {
  name: string;
  description: string;
  tnc: string;
  event_date: string;
  start_time: string;
  end_time: string;
  lokasi_arena: string;
  address: string;
  city: string;
  map_link: string;
  link_group: string;
  field_id: number | null;
  type: 'free' | 'paid';
  visibility: 'public' | 'private';
  status?: string;
  config: EventConfig;
  custom_form?: CustomFormField[];
}

// Pagination meta
export interface PaginationMeta {
  limit: number;
  page: number;
  total: number;
  tab?: string;
}

// Dashboard pagination
export interface DashboardPagination {
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// Public participant info
export interface PublicParticipant {
  name: string;
  username?: string;
  email?: string;
  avatar: string;
  role: string;
  status: string;
  joined_at: string;
}

// Public event participants response
export interface PublicEventParticipantsData {
  event_name: string;
  slug: string;
  event_date: string;
  start_time: string;
  end_time: string;
  lokasi_arena: string;
  sport_category: string;
  player_slots: number;
  visibility: 'public' | 'private';
  total_participants: number;
  participants: PublicParticipant[];
}

// Dashboard stats
export interface DashboardStats {
  total_slots: number;
  filled_slots: number;
  slot_percentage: number;
  profit: number;
  expenses: number;
  income: number;
}

// Dashboard participant
export interface DashboardParticipant {
  id: number;
  name: string;
  wa_number: string;
  email: string;
  role: string;
  price: number;
  status: string;
  payment_status: 'paid' | 'unpaid';
  joined_at: string;
}

// Dashboard data response
export interface DashboardData {
  slug: string;
  name: string;
  visibility: 'public' | 'private';
  lokasi_arena: string;
  sport_category: SportCategoryInfo;
  event_date: string;
  start_time: string;
  end_time: string;
  stats: DashboardStats;
  participants: DashboardParticipant[];
  pagination: DashboardPagination;
  live_list_url: string;
}

// API response with pagination
interface PaginatedApiResponse<T> {
  success: boolean;
  message: string;
  data: T[];
  meta: PaginationMeta;
}

export interface EventJoinedResponse {
  joined: boolean;
  status?: string;
  success: boolean;
  message: string;
}

export interface JoinMabarPayload {
  email?: string;
  role: string;
  team?: string;
  wa_number?: string;
  custom_form_answer?: CustomFormAnswerItem[];
}

export interface JoinMabarResponse {
  success: boolean;
  message: string;
  data?: {
    status: string;
    role: string;
    price: number;
    payment_checkout_payload?: any;
    invoice_number?: string;
    is_awal_qris?: boolean;
  };
}

class MabarService {
  /**
   * Create a new mabar event
   * @param payload - Event data to create
   * @returns Promise with created event data
   */
  async createEvent(payload: CreateEventPayload): Promise<MabarEvent> {
    try {
      const response = await api.post<ApiResponse<MabarEvent>>('/mabar/events', payload);

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Gagal membuat event');
      }

      return response.data.data as MabarEvent;
    } catch (error: any) {
      // Handle axios error response
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Get public list of mabar events with optional filters
   * @param params - Query parameters for filtering
   * @returns Promise with list of public events and pagination meta
   */
  async getPublicEvents(
    params: PublicEventsQueryParams = {}
  ): Promise<{ data: PublicMabarEvent[]; meta: PaginationMeta }> {
    try {
      // Build query string
      const queryParams = new URLSearchParams();

      // Always include page and limit
      queryParams.append('page', String(params.page || 1));
      queryParams.append('limit', String(params.limit || 12));
      if (params.lp != undefined) {
        queryParams.append('lp', 'true');
      }

      // Add optional filters only if they have values
      if (params.city) {
        queryParams.append('city', params.city);
      }
      if (params.sport_category) {
        queryParams.append('sport_category', params.sport_category);
      }
      if (params.date_from) {
        queryParams.append('date_from', params.date_from);
      }
      if (params.date_to) {
        queryParams.append('date_to', params.date_to);
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }
      if (params.host_id) {
        queryParams.append('host_id', String(params.host_id));
      }
      if (params.type) {
        queryParams.append('type', params.type);
      }
      if (params.sport_type) {
        queryParams.append('sport_type', params.sport_type);
      }

      const response = await api.get<PaginatedApiResponse<PublicMabarEvent>>(
        `/mabar/events?${queryParams.toString()}`
      );

      if (response.data?.success) {
        return {
          data: response.data.data || [],
          meta: response.data.meta,
        };
      }

      return { data: [], meta: { limit: 12, page: 1, total: 0 } };
    } catch (error: any) {
      console.error('Failed to get public events:', error);
      return { data: [], meta: { limit: 12, page: 1, total: 0 } };
    }
  }

  /**
   * Get list of mabar events (legacy method)
   * @param page - Page number
   * @param limit - Items per page
   * @returns Promise with list of events
   */
  async getEvents(page: number = 1, limit: number = 10): Promise<MabarEvent[]> {
    try {
      const response = await api.get<ApiResponse<{ events: MabarEvent[] }>>(
        `/mabar/events?page=${page}&limit=${limit}`
      );

      if (response.data?.success && response.data?.data?.events) {
        return response.data.data.events;
      }

      return [];
    } catch (error) {
      console.error('Failed to get events:', error);
      return [];
    }
  }

  /**
   * Get event by ID
   * @param id - Event ID
   * @returns Promise with event data
   */
  async getEventById(id: number): Promise<MabarEvent | null> {
    try {
      const response = await api.get<ApiResponse<MabarEvent>>(`/mabar/events/${id}`);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Failed to get event:', error);
      return null;
    }
  }

  /**
   * Get event by slug (public or as participant)
   * @param slug - Event slug
   * @returns Promise with event detail data
   */
  async getEventBySlug(slug: string): Promise<EventDetailResponse | null> {
    try {
      const response = await api.get<ApiResponse<EventDetailResponse>>(`/mabar/events/${slug}`);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return null;
    } catch (error: any) {
      console.error('Failed to get event:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Get games list for an event (customer/community, requires auth)
   * @param slug - Event slug
   * @returns Promise with list of games
   */
  async getGamesByEventSlug(slug: string): Promise<GameListItem[]> {
    try {
      const response = await api.get<ApiResponse<GameListItem[]>>(`/mabar/events/${slug}/games`);
      return response.data?.data ?? [];
    } catch (error: any) {
      console.error('Failed to get event games:', error);
      return [];
    }
  }

  async getStandingsByEventSlug(slug: string): Promise<StandingItem[]> {
    try {
      const response = await api.get<ApiResponse<StandingItem[]>>(
        `/mabar/events/${slug}/standings`
      );
      return response.data?.data ?? [];
    } catch {
      return [];
    }
  }

  /**
   * Get games list for an event (public, no login required)
   * @param slug - Event slug
   * @returns Promise with list of games
   */
  async getGamesByEventSlugPublic(slug: string): Promise<GameListItem[]> {
    try {
      const response = await api.get<ApiResponse<GameListItem[]>>(
        `/mabar/events/${slug}/games-public`
      );
      return response.data?.data ?? [];
    } catch (error: any) {
      console.error('Failed to get event games (public):', error);
      throw error;
    }
  }

  /**
   * Get game detail by slug (public, no login required)
   * @param slug - Game slug
   * @returns Promise with game detail (timeline, lineups)
   */
  async getGameBySlug(slug: string): Promise<GameDetailResponse> {
    const response = await api.get<ApiResponse<GameDetailResponse>>(`/mabar/games/${slug}`);
    if (!response.data?.success || !response.data?.data) {
      throw new Error('Gagal mengambil data game');
    }
    return response.data.data;
  }

  /**
   * Get my stats for an event (goals, assists, win_rate, matches) - futsal/mini-soccer only
   * @param slug - Event slug
   * @returns Promise with stats
   */
  async getEventMyStats(slug: string): Promise<EventMyStatsResponse> {
    try {
      const response = await api.get<ApiResponse<EventMyStatsResponse>>(
        `/mabar/events/${slug}/my-stats`
      );
      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }
      return { goals: 0, assists: 0, win_rate: 0, matches: 0 };
    } catch (error: any) {
      console.error('Failed to get event my stats:', error);
      return { goals: 0, assists: 0, win_rate: 0, matches: 0 };
    }
  }

  /**
   * Get my hosted event by slug (for editing)
   * @param slug - Event slug
   * @returns Promise with event detail data
   */
  async getMyEventBySlug(slug: string): Promise<EventDetailResponse | null> {
    try {
      const response = await api.get<ApiResponse<EventDetailResponse>>(`/mabar/my-events/${slug}`);

      if (response.data?.success && response.data?.data) {
        return response.data.data;
      }

      return null;
    } catch (error: any) {
      console.error('Failed to get event:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Update an existing mabar event
   * @param slug - Event slug
   * @param payload - Event data to update
   * @returns Promise with updated event data
   */
  async updateEvent(slug: string, payload: UpdateEventPayload): Promise<EventDetailResponse> {
    try {
      const response = await api.put<ApiResponse<EventDetailResponse>>(
        `/mabar/events/${slug}`,
        payload
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Gagal mengupdate event');
      }

      return response.data.data as EventDetailResponse;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Get my participations (events I joined)
   * @param tab - Tab filter: 'upcoming' or 'history'
   * @param page - Page number
   * @param limit - Items per page
   * @returns Promise with participation events and meta
   */
  async getMyParticipations(
    tab: 'upcoming' | 'history' = 'upcoming',
    page: number = 1,
    limit: number = 12
  ): Promise<{ data: ParticipationEvent[]; meta: PaginationMeta }> {
    try {
      const response = await api.get<PaginatedApiResponse<ParticipationEvent>>(
        `/mabar/my-participations?tab=${tab}&page=${page}&limit=${limit}`
      );

      if (response.data?.success) {
        return {
          data: response.data.data || [],
          meta: response.data.meta,
        };
      }

      return { data: [], meta: { limit, page, total: 0, tab } };
    } catch (error: any) {
      console.error('Failed to get participations:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Get my hosted events
   * @param page - Page number
   * @param limit - Items per page
   * @returns Promise with hosted events and meta
   */
  async getMyHostedEvents(
    page: number = 1,
    limit: number = 12
  ): Promise<{ data: ParticipationEvent[]; meta: PaginationMeta }> {
    try {
      const response = await api.get<PaginatedApiResponse<ParticipationEvent>>(
        `/mabar/my-events?page=${page}&limit=${limit}`
      );

      if (response.data?.success) {
        return {
          data: response.data.data || [],
          meta: response.data.meta,
        };
      }

      return { data: [], meta: { limit, page, total: 0 } };
    } catch (error: any) {
      console.error('Failed to get hosted events:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Leave/cancel participation from an event
   * @param slug - Event slug
   * @returns Promise with success status
   */
  async leaveEvent(slug: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.delete<ApiResponse<null>>(`/mabar/events/${slug}/leave`);

      return {
        success: response.data?.success || false,
        message: response.data?.message,
      };
    } catch (error: any) {
      console.error('Failed to leave event:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Delete/cancel an event (for host only)
   * @param slug - Event slug
   * @returns Promise with success status
   */
  async deleteEvent(slug: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.delete<ApiResponse<null>>(`/mabar/events/${slug}`);

      return {
        success: response.data?.success || false,
        message: response.data?.message,
      };
    } catch (error: any) {
      console.error('Failed to delete event:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Get event dashboard for host
   * @param slug - Event slug
   * @param page - Page number
   * @param limit - Items per page
   * @returns Promise with dashboard data
   */
  async getEventDashboard(
    slug: string,
    page: number = 1,
    limit: number = 10
  ): Promise<DashboardData> {
    try {
      const response = await api.get<ApiResponse<DashboardData>>(
        `/mabar/my-events/${slug}/dashboard?page=${page}&limit=${limit}`
      );

      if (!response.data?.success || !response.data.data) {
        throw new Error(response.data?.message || 'Failed to load dashboard');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to get event dashboard:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Export event participants as PDF
   * @param slug - Event slug
   * @returns Promise with blob data
   */
  async exportEventPdf(slug: string): Promise<Blob> {
    try {
      const response = await api.get(`/mabar/my-events/${slug}/export/pdf`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error: any) {
      console.error('Failed to export PDF:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to export PDF');
    }
  }

  /**
   * Export event participants as XLSX
   * @param slug - Event slug
   * @returns Promise with blob data
   */
  async exportEventXlsx(slug: string): Promise<Blob> {
    try {
      const response = await api.get(`/mabar/my-events/${slug}/export/xlsx`, {
        responseType: 'blob',
      });

      return response.data;
    } catch (error: any) {
      console.error('Failed to export XLSX:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to export Excel');
    }
  }

  /**
   * Delete a participant from event
   * @param slug - Event slug
   * @param participantId - Participant ID
   * @returns Promise with success status
   */
  async deleteParticipant(
    slug: string,
    participantId: number
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.delete<ApiResponse<null>>(
        `/events/${slug}/participants/${participantId}`
      );

      return {
        success: response.data?.success || false,
        message: response.data?.message,
      };
    } catch (error: any) {
      console.error('Failed to delete participant:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Update participant payment status
   * @param slug - Event slug
   * @param participantId - Participant ID
   * @param paymentStatus - New payment status ('paid' or 'unpaid')
   * @returns Promise with success status
   */
  async updatePaymentStatus(
    slug: string,
    participantId: number,
    paymentStatus: 'paid' | 'unpaid'
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.patch<ApiResponse<null>>(
        `/mabar/my-events/${slug}/participants/${participantId}/payment-status`,
        { payment_status: paymentStatus }
      );

      return {
        success: response.data?.success || false,
        message: response.data?.message,
      };
    } catch (error: any) {
      console.error('Failed to update payment status:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Get public event participants list (no auth required)
   * @param slug - Event slug
   * @returns Promise with event participants data
   */
  async getEventParticipants(slug: string): Promise<PublicEventParticipantsData> {
    try {
      const response = await api.get<ApiResponse<PublicEventParticipantsData>>(
        `/mabar/events/${slug}/participants`
      );

      if (!response.data?.success || !response.data.data) {
        throw new Error(response.data?.message || 'Failed to load participants');
      }

      return response.data.data;
    } catch (error: any) {
      console.error('Failed to get event participants:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  async checkEventJoined(slug: string): Promise<EventJoinedResponse> {
    try {
      const { data } = await api.get(`/client/mabar/joined/${slug}`);
      return data as EventJoinedResponse;
    } catch (error: any) {
      console.error('Failed to check event joined status:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  async joinEvent(slug: string, payload: JoinMabarPayload): Promise<JoinMabarResponse> {
    const response = await api.post<JoinMabarResponse>(`/mabar/events/${slug}/join`, payload);
    return response.data;
  }

  async renderShareRecap(
    slug: string,
    data: {
      eventName: string;
      eventDateFormatted: string;
      stats: { goals: number; assists: number; matches: number; win_rate: number };
      playerName: string;
      playerAvatarUrl?: string;
      template?: string;
    }
  ): Promise<Blob> {
    try {
      const response = await api.post<Blob>(`/mabar/events/${slug}/share-recap/render`, data, {
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: unknown) {
      throw await recapDownloadError(error, 'Gagal mengunduh gambar statistik');
    }
  }

  async renderShareRecapV2(
    slug: string,
    data: {
      eventName: string;
      eventDateFormatted: string;
      scoreText?: string;
      winnerName?: string;
      stats: {
        goals: number;
        assists: number;
        matches: number;
        win_rate?: number;
        wins?: number;
        point?: number;
        point_loss?: number;
        is_racket?: boolean;
        rank?: number;
        clean_sheet?: number;
        cleansheet?: number;
        cleansheets?: number;
        key_passes?: number;
        saves?: number;
        clearances?: number;
        fouls?: number;
        motm?: number;
        freestyle?: number;
        winstreak?: number;
        stat_keys?: string[];
      };
      playerName: string;
      playerAvatarUrl?: string;
      template?: string;
      sportCategory?: string;
      location?: string;
      deltaMMR?: number | null;
      bgImage?: string;
      standingsJson?: string;
    }
  ): Promise<Blob> {
    try {
      const response = await api.post<Blob>(`/mabar/events/${slug}/share-recap/render/v2`, data, {
        responseType: 'blob',
      });
      return response.data as Blob;
    } catch (error: unknown) {
      throw await recapDownloadError(error, 'Gagal mengunduh gambar statistik');
    }
  }

  async getTransactionCheckout(orderNumber: string, force: boolean = false): Promise<any> {
    try {
      const url = `/client/mabar/transaction/${orderNumber}/checkout${force ? '?force=true' : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get transaction checkout:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  async getTransactionStatus(orderNumber: string): Promise<any> {
    try {
      const response = await api.get(`/client/mabar/transaction/${orderNumber}/status`);
      return response.data;
    } catch (error: any) {
      console.error('Failed to get transaction status:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }
}

// Export singleton instance
export const mabarService = new MabarService();
export default mabarService;
